import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Upload } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useFileUpload } from '../hooks/useSalesDocs';
import { deriveFileName } from '../constants';
import { usePipelineList, usePipelineStages } from '@/features/pipeline/hooks/usePipeline';
import { useSalesDocFolders } from '../hooks/useSalesDocs';
import type { CreateSalesDocPayload, SalesDoc, UpdateSalesDocPayload, SalesDocType, SalesDocStatus } from '../types';

interface SalesDocFormModalProps {
  open: boolean;
  doc?: SalesDoc | null; // null = create
  loading?: boolean;
  onClose: () => void;
  onCreate: (payload: CreateSalesDocPayload) => void;
  onUpdate: (id: string, payload: UpdateSalesDocPayload) => void;
}

export function SalesDocFormModal({ open, doc, loading, onClose, onCreate, onUpdate }: SalesDocFormModalProps) {
  const isEdit = !!doc;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [type, setType] = useState<SalesDocType | ''>('');
  const [status, setStatus] = useState<SalesDocStatus>('draft');
  const [amount, setAmount] = useState<number | ''>('');
  const [relatedToId, setRelatedToId] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [folderId, setFolderId] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useFileUpload();
  const { data: folders = [] } = useSalesDocFolders();

  // One picker over the unified pipeline, not a Lead list and a Deal list —
  // a record is one thing, whichever stage it happens to be at.
  const { data: pipelineData } = usePipelineList({ limit: 1000, offset: 0 });
  const { data: stagesData } = usePipelineStages();
  const records = pipelineData?.items || [];
  const sideOfSelected = stagesData?.stages.find(
    (s) => s.key === records.find((r) => r.id === relatedToId)?.stage
  )?.side;
  // SalesDoc.related_to_type is still a 'lead' | 'deal' column — derived from
  // the selected record's current stage rather than asked of the user.
  const relatedToType: 'lead' | 'deal' | '' = relatedToId ? (sideOfSelected === 'lead' ? 'lead' : 'deal') : '';

  // Reset the form whenever the modal opens or the target doc changes, adjusting
  // state during render (via previous-value tracking) instead of in an effect.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevDoc, setPrevDoc] = useState(doc);
  if (open !== prevOpen || doc !== prevDoc) {
    setPrevOpen(open);
    setPrevDoc(doc);
    if (open) {
      setTitle(doc?.title ?? '');
      setDescription(doc?.description ?? '');
      setUrl(doc?.url ?? '');
      setType(doc?.type ?? '');
      setStatus(doc?.status ?? 'draft');
      setAmount(doc?.amount ?? '');
      setRelatedToId(doc?.related_to_id ?? '');
      setReferenceNumber(doc?.reference_number ?? '');
      setValidUntil(doc?.valid_until ? doc.valid_until.split('T')[0] : '');
      setFolderId(doc?.folderId ?? '');
    }
  }

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    upload.mutate(file, {
      onSuccess: (returnedUrl) => {
        setUrl(returnedUrl);
        if (!title.trim()) setTitle(file.name);
        toast.success('File uploaded.');
      },
      onError: () => toast.error('Upload failed.'),
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !type) {
      toast.error('Title and Type are required.');
      return;
    }
    if (!url.trim()) {
      toast.error('Add a document URL or upload a file.');
      return;
    }
    const payload: any = {
      title: title.trim(),
      description: description.trim(),
      url: url.trim(),
      type,
      status,
      amount: amount === '' ? 0 : Number(amount),
      related_to_type: relatedToType || null,
      related_to_id: relatedToId || null,
      reference_number: referenceNumber || '',
      valid_until: validUntil || null,
      folderId: folderId || null,
    };

    if (isEdit) {
      onUpdate(doc!.id, payload);
    } else {
      onCreate(payload);
    };
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Document' : 'New Document'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" form="salesdoc-form" loading={loading}>{isEdit ? 'Save Changes' : 'Add Document'}</Button>
        </>
      }
    >
      <form id="salesdoc-form" onSubmit={submit} className="space-y-4">
        <Select
          label="Document Type *"
          name="doc-type"
          value={type}
          onChange={(e) => setType(e.target.value as SalesDocType)}
          options={[
            { value: '', label: 'Select type...' },
            { value: 'proposal', label: 'Proposal' },
            { value: 'quotation', label: 'Quotation' },
            { value: 'proforma', label: 'Proforma' },
            { value: 'scope_of_services', label: 'Scope of Services' },
            { value: 'invoice', label: 'Invoice' },
            { value: 'other', label: 'Other' },
          ]}
          required
        />
        
        {type && (
          <Select
            label="Status"
            name="doc-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as SalesDocStatus)}
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'sent', label: 'Sent' },
              { value: 'accepted', label: 'Accepted' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'expired', label: 'Expired' },
              { value: 'finalized', label: 'Finalized' },
              { value: 'paid', label: 'Paid' },
              { value: 'overdue', label: 'Overdue' },
            ]}
          />
        )}

        {(type === 'invoice' || type === 'quotation') && (
          <Input
            type="number"
            label="Amount ($)"
            name="doc-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value ? Number(e.target.value) : '')}
            placeholder="0.00"
            min="0"
            step="0.01"
            required
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Reference Number (Optional)"
            name="reference-number"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            placeholder="e.g. INV-001"
          />
          <Input
            type="date"
            label="Valid Until (Optional)"
            name="valid-until"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </div>

        <Select
          label="Related To (Optional)"
          name="doc-related-record"
          value={relatedToId}
          onChange={(e) => setRelatedToId(e.target.value)}
          options={[
            { value: '', label: 'None' },
            ...records.map((r) => ({ value: r.id, label: r.title || r.name })),
          ]}
        />

        <Input label="Title *" name="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title…" required />

        <Select
          label="Folder (Optional)"
          name="doc-folder"
          value={folderId}
          onChange={(e) => setFolderId(e.target.value)}
          options={[
            { value: '', label: 'Unfiled' },
            ...folders.map((f) => ({ value: f.id, label: f.name })),
          ]}
        />

        <div>
          <label htmlFor="doc-desc" className="mb-1.5 block text-body-sm font-medium text-on-surface">Description</label>
          <textarea
            id="doc-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description…"
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        <div>
          <Input
            label="Document URL *"
            name="doc-url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://… or upload a file"
            required
          />
          <div className="mt-2 flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" loading={upload.isPending} onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> Upload file
            </Button>
            {url && <span className="truncate text-body-sm text-on-surface-variant">{deriveFileName(url)}</span>}
          </div>
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
        </div>
      </form>
    </Modal>
  );
}
